// src/oura/components/aeo/AssessmentProgress.tsx
//
// AC: "Polling view: progress bar from stages_complete/stages_total + stage
// label; polls ~5–10s until terminal." (#3065) Polls every ~7s and always
// clears its interval on unmount or once a terminal status arrives.

import { useEffect, useRef, useState } from "react";
import { Loader2Icon, ClockIcon } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../primitives/Card";
import { getAssessment, type AeoAssessment } from "@service/aeo";
import { assessmentStageLabel } from "../../stageLabels";
import { StageChecklist, EtaLine } from "./StageChecklist";

const POLL_MS = 7000;

export function AssessmentProgress({
    assessmentId,
    onTerminal,
}: {
    assessmentId: string;
    /** Fires once when status leaves queued|running. Polling stops immediately after. */
    onTerminal: (assessment: AeoAssessment) => void;
}) {
    const [assessment, setAssessment] = useState<AeoAssessment | null>(null);
    const [error, setError] = useState<string | null>(null);
    const onTerminalRef = useRef(onTerminal);
    onTerminalRef.current = onTerminal;

    useEffect(() => {
        let cancelled = false;
        let timer: number | undefined;

        const poll = async () => {
            try {
                const a = await getAssessment(assessmentId);
                if (cancelled) return;
                setAssessment(a);
                if (a.status === "queued" || a.status === "running") {
                    timer = window.setTimeout(poll, POLL_MS);
                } else {
                    onTerminalRef.current(a);
                }
            } catch {
                if (!cancelled) setError("Lost the connection to this assessment. Retrying…");
                if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
            }
        };

        void poll();
        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [assessmentId]);

    const total = assessment?.stages_total ?? 0;
    const complete = assessment?.stages_complete ?? 0;
    const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
    // Open by default here, unlike the toast: this page exists to be watched,
    // and there is room. The toast has to stay small enough for several scans
    // at once.
    const stages = assessment?.stages ?? [];

    return (
        <Card>
            <CardHeader
                title="Running assessment"
                description="A full run typically takes 25–35 minutes: every question is asked in the real ChatGPT app, one at a time. This view updates on its own — no need to keep it open."
                icon={ClockIcon}
            />
            <CardBody className="pt-2">
                <div className="flex items-center gap-3">
                    <Loader2Icon className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-900">
                                {/* The stage NAME, not the raw key — this read
                                    "run_engines" and "audit_fallback" to the
                                    operator until #3535. */}
                                {assessmentStageLabel(assessment) || "Starting…"}
                            </span>
                            <span className="tabular-nums text-gray-500">
                                {complete}/{total || "…"}
                            </span>
                        </div>
                        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-blue-600 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <EtaLine eta={assessment?.eta} className="mt-1.5" />
                    </div>
                </div>
                {/* Which steps are done, not just how many. Order comes from
                    the backend array — see StageChecklist. Absent on an older
                    backend, and then this renders nothing and the bar above is
                    unchanged. */}
                {stages.length > 0 && <StageChecklist stages={stages} className="mt-4" />}
                {error && <p className="mt-3 text-xs text-amber-600">{error}</p>}
                <p className="mt-3 text-xs text-gray-500">
                    {assessment?.domain} · assessment {assessmentId}
                </p>
            </CardBody>
        </Card>
    );
}
